import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col, Card, Form, Button, Alert, Image } from 'react-bootstrap';
import { useAuth } from '../context/AuthContext';
import { apiClient, API_HOST_BASE, getImageUrl } from '../utils/apiUtils';
import ChangePasswordModal from './ChangePasswordModal';
import './Profile.css';

const Profile = () => {
  const { user, updateUser } = useAuth();
  const [formData, setFormData] = useState({
    fullname: user?.fullname || '',
    department_college: user?.department_college || ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [photoLoading, setPhotoLoading] = useState(false);
  const [photoError, setPhotoError] = useState('');
  const [photoSuccess, setPhotoSuccess] = useState('');
  const fileInputRef = useRef(null);
  
  // Signature upload states
  const [signatureLoading, setSignatureLoading] = useState(false);
  const [signatureError, setSignatureError] = useState('');
  const [signatureSuccess, setSignatureSuccess] = useState('');
  const signatureInputRef = useRef(null);

  // Change password modal state
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);

  // Debug: Log user data to see what's available
  useEffect(() => {
    console.log('Profile - User data:', user);
    console.log('Profile - Account status:', user?.account_status);
  }, [user]);

  // Load user data on component mount
  useEffect(() => {
    const loadUserData = async () => {
      if (!user) {
        try {
          setLoading(true);
          const response = await apiClient.get('/auth/me', { timeout: 10000 });
          updateUser && updateUser(response.data);
        } catch (error) {
          console.error('Error loading user data:', error);
          setError('Failed to load user data. Please refresh the page.');
        } finally {
          setLoading(false);
        }
      }
    };

    loadUserData();
  }, [user, updateUser]);

  // Set global timeout for all API calls in this component
  useEffect(() => {
    // Set a global timeout for axios requests
    const originalTimeout = apiClient.defaults.timeout;
    apiClient.defaults.timeout = 15000; // 15 seconds
    
    // Cleanup function to restore original timeout
    return () => {
      apiClient.defaults.timeout = originalTimeout;
    };
  }, []);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // Update profile with timeout
      await apiClient.put('/users/profile', {
        fullname: formData.fullname,
        email: user?.email || '',
        phone: user?.phone || '',
        organization: formData.department_college, // map to server's expected field
        address: user?.address || ''
      }, {
        timeout: 10000 // 10 second timeout
      });

      // Fetch updated user data with timeout
      const userResponse = await apiClient.get('/auth/me', {
        timeout: 10000 // 10 second timeout
      });
      
      updateUser && updateUser(userResponse.data);
      setFormData({
        fullname: userResponse.data?.fullname || '',
        department_college: userResponse.data?.department_college || ''
      });
      setSuccess('Profile updated successfully!');
    } catch (error) {
      console.error('Profile update error:', error);
      
      // Handle timeout errors specifically
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        setError('Request timed out. Please try again.');
      } else {
        const serverMsg = error?.response?.data?.error;
        const validationMsg = Array.isArray(error?.response?.data?.errors)
          ? error.response.data.errors.map(e => e.msg).join(', ')
          : '';
        setError(serverMsg || validationMsg || 'Failed to update profile');
      }
    } finally {
      setLoading(false);
    }
  };


  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setPhotoError('Please select an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setPhotoError('File size must be less than 5MB');
      return;
    }

    setPhotoLoading(true);
    setPhotoError('');
    setPhotoSuccess('');

    const formData = new FormData();
    formData.append('profilePhoto', file);

    try {
      const response = await apiClient.post(`/users/${user.user_id}/photo`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        timeout: 15000 // 15 second timeout for file uploads
      });

      // Fetch updated user data with timeout
      const userResponse = await apiClient.get('/auth/me', {
        timeout: 10000 // 10 second timeout
      });
      updateUser(userResponse.data);
      
      setPhotoSuccess('Profile photo updated successfully!');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error) {
      console.error('Photo upload error:', error);
      
      // Handle timeout errors specifically
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        setPhotoError('Upload timed out. Please try again.');
      } else {
        setPhotoError(error.response?.data?.error || 'Failed to upload profile photo');
      }
    } finally {
      setPhotoLoading(false);
    }
  };

  const handlePhotoDelete = async () => {
    if (!user?.profile_photo) {
      setPhotoError('No profile photo to delete');
      return;
    }

    setPhotoLoading(true);
    setPhotoError('');
    setPhotoSuccess('');

    try {
      await apiClient.delete(`/users/${user.user_id}/photo`, {
        timeout: 10000 // 10 second timeout
      });
      
      // Fetch updated user data with timeout
      const userResponse = await apiClient.get('/auth/me', {
        timeout: 10000 // 10 second timeout
      });
      updateUser(userResponse.data);
      
      setPhotoSuccess('Profile photo deleted successfully!');
    } catch (error) {
      console.error('Photo delete error:', error);
      
      // Handle timeout errors specifically
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        setPhotoError('Delete timed out. Please try again.');
      } else {
        setPhotoError(error.response?.data?.error || 'Failed to delete profile photo');
      }
    } finally {
      setPhotoLoading(false);
    }
  };

  const handleSignatureUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setSignatureError('Please select an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setSignatureError('File size must be less than 5MB');
      return;
    }

    setSignatureLoading(true);
    setSignatureError('');
    setSignatureSuccess('');

    const formData = new FormData();
    formData.append('signature', file);

    try {
      const response = await apiClient.post(`/users/${user.user_id}/signature`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        timeout: 15000 // 15 second timeout for file uploads
      });

      // Fetch updated user data with timeout
      const userResponse = await apiClient.get('/auth/me', {
        timeout: 10000 // 10 second timeout
      });
      updateUser(userResponse.data);
      
      setSignatureSuccess('Digital signature updated successfully!');
      if (signatureInputRef.current) {
        signatureInputRef.current.value = '';
      }
    } catch (error) {
      console.error('Signature upload error:', error);
      
      // Handle timeout errors specifically
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        setSignatureError('Upload timed out. Please try again.');
      } else {
        setSignatureError(error.response?.data?.error || 'Failed to upload digital signature');
      }
    } finally {
      setSignatureLoading(false);
    }
  };

  const handleSignatureDelete = async () => {
    if (!user?.signature_path) {
      setSignatureError('No digital signature to delete');
      return;
    }

    setSignatureLoading(true);
    setSignatureError('');
    setSignatureSuccess('');

    try {
      await apiClient.delete(`/users/${user.user_id}/signature`, {
        timeout: 10000 // 10 second timeout
      });
      
      // Fetch updated user data with timeout
      const userResponse = await apiClient.get('/auth/me', {
        timeout: 10000 // 10 second timeout
      });
      updateUser(userResponse.data);
      
      setSignatureSuccess('Digital signature deleted successfully!');
    } catch (error) {
      console.error('Signature delete error:', error);
      
      // Handle timeout errors specifically
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        setSignatureError('Delete timed out. Please try again.');
      } else {
        setSignatureError(error.response?.data?.error || 'Failed to delete digital signature');
      }
    } finally {
      setSignatureLoading(false);
    }
  };


  const getStatusBadgeColor = (status) => {
    if (!status) return 'success'; // Default to green for missing status
    
    const statusLower = status.toLowerCase().trim();
    
    switch (statusLower) {
      case 'active':
        return 'success'; // Green text for active status
      case 'inactive':
        return 'danger';  // Red text
      case 'pending':
        return 'warning'; // Yellow text
      default:
        return 'success'; // Default to green text for unknown statuses
    }
  };

  // Show loading state if user data is not available
  if (loading && !user) {
    return (
      <div className="container-fluid px-3 px-md-4 py-3">
        <div className="text-center">
          <div className="spinner-border text-primary mb-3" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p>Loading profile...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid px-3 px-md-4 py-3">
      <div className="row">
        <div className="col-12">
            {/* Profile Photo Section */}
            <Card className="border-0 shadow-sm mb-4">
              <Card.Header className="bg-white border-bottom">
                <h5 className="mb-0">
                  <i className="bi bi-camera me-2"></i>
                  Profile Photo
                </h5>
              </Card.Header>
              <Card.Body className="p-4">
                {photoError && (
                  <Alert variant="danger" dismissible onClose={() => setPhotoError('')}>
                    {photoError}
                  </Alert>
                )}

                {photoSuccess && (
                  <Alert variant="success" dismissible onClose={() => setPhotoSuccess('')}>
                    {photoSuccess}
                  </Alert>
                )}

                <Row className="align-items-center">
                  <Col md={4} className="text-center mb-3 mb-md-0">
                    <div className="profile-photo-display">
                      {user?.profile_photo ? (
                        <div>
                          <Image 
                            src={getImageUrl(user.profile_photo, 'profile-photos')}
                            alt="Profile" 
                            roundedCircle 
                            width={120} 
                            height={120}
                            className="border"
                            onError={(e) => {
                              console.error('Profile image failed to load:', e.target.src);
                              console.error('API_HOST_BASE:', API_HOST_BASE);
                              console.error('user.profile_photo:', user.profile_photo);
                              e.target.style.display = 'none';
                              e.target.nextElementSibling.style.display = 'flex';
                            }}
                          />
                          <div className="profile-photo-placeholder" style={{ display: 'none' }}>
                            <i className="bi bi-person-fill"></i>
                            <small className="d-block text-muted mt-2">Photo failed to load</small>
                          </div>
                        </div>
                      ) : (
                        <div className="profile-photo-placeholder">
                          <i className="bi bi-person-fill"></i>
                        </div>
                      )}
                    </div>
                  </Col>
                  <Col md={8}>
                    <div className="mb-3">
                      <Form.Label>Upload New Photo</Form.Label>
                      <Form.Control
                        type="file"
                        accept="image/*"
                        onChange={handlePhotoUpload}
                        ref={fileInputRef}
                        disabled={photoLoading}
                      />
                      <Form.Text className="text-muted">
                        Supported formats: JPG, PNG, GIF. Max size: 5MB
                      </Form.Text>
                    </div>
                    
                    {user?.profile_photo && (
                      <div>
                        <Button 
                          variant="outline-danger" 
                          size="sm"
                          onClick={handlePhotoDelete}
                          disabled={photoLoading}
                        >
                          {photoLoading ? (
                            <>
                              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                              Deleting...
                            </>
                          ) : (
                            <>
                              <i className="bi bi-trash me-2"></i>
                              Delete Photo
                            </>
                          )}
                        </Button>
                      </div>
                    )}
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            <Card className="border-0 shadow-sm">
              <Card.Header className="bg-white border-bottom">
                <h4 className="mb-0">
                  <i className="bi bi-info-circle me-2"></i>
                  Account Information
                </h4>
              </Card.Header>
              <Card.Body className="p-4">
                {error && (
                  <Alert variant="danger" dismissible onClose={() => setError('')}>
                    {error}
                  </Alert>
                )}

                {success && (
                  <Alert variant="success" dismissible onClose={() => setSuccess('')}>
                    {success}
                  </Alert>
                )}

                <Form onSubmit={handleSubmit}>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Full Name</Form.Label>
                        <Form.Control
                          type="text"
                          name="fullname"
                          value={formData.fullname}
                          onChange={handleChange}
                          placeholder="Enter your full name"
                          required
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Department/College</Form.Label>
                        <Form.Select
                          name="department_college"
                          value={formData.department_college}
                          onChange={handleChange}
                          required
                        >
                          <option value="">Select department/college</option>
                          <option value="CET">CET</option>
                          <option value="CAS">CAS</option>
                          <option value="CABE">CABE</option>
                          <option value="CTE">CTE</option>
                          <option value="CICS">CICS</option>
                        </Form.Select>
                      </Form.Group>
                    </Col>
                  </Row>

                  {/* Static account meta previously in Account Information */}
                  <Row className="mt-2">
                    <Col md={6}>
                      <div className="mb-3">
                        <label className="form-label text-muted small">User ID</label>
                        <p className="mb-0 fw-bold">{user?.user_id}</p>
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="mb-3">
                        <label className="form-label text-muted small">Account Created</label>
                        <p className="mb-0 fw-bold">
                          {user?.date_registered ? new Date(user.date_registered).toLocaleDateString() : 'N/A'}
                        </p>
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="mb-3">
                        <label className="form-label text-muted small">Role</label>
                        <p className="mb-0 fw-bold">{user?.role || 'N/A'}</p>
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="mb-3">
                        <label className="form-label text-muted small">Account Status</label>
                        <p className="mb-0">
                          <span className={`text-${getStatusBadgeColor(user?.account_status)} fw-bold`}>
                            {user?.account_status || 'Active'}
                          </span>
                        </p>
                      </div>
                    </Col>
                  </Row>

                  <div className="d-flex justify-content-end mb-4">
                    <Button
                      variant="primary"
                      type="submit"
                      disabled={loading}
                    >
                      {loading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          Updating...
                        </>
                      ) : (
                        'Update Profile'
                      )}
                    </Button>
                  </div>

                  {/* Email Address Display */}
                  <div className="mb-4 border-top pt-4">
                    <h6 className="mb-2">Email Address</h6>
                    <div className="p-3 bg-light rounded border">
                      <div className="d-flex align-items-center">
                        <i className="bi bi-envelope me-2 text-muted"></i>
                        <span className="fw-medium">{user?.email || 'No email address'}</span>
                      </div>
                      <small className="text-muted">This e-mail address is linked to your account and cannot be changed. Please contact an administrator if you wish to change this.</small>
                    </div>
                  </div>

                  {/* Change Password Section */}
                  <div className="mb-4 border-top pt-4">
                    <h6 className="mb-3">
                      <i className="bi bi-shield-lock me-2"></i>
                      Password
                    </h6>
                    <Button
                      variant="outline-primary"
                      onClick={() => setShowChangePasswordModal(true)}
                    >
                      <i className="bi bi-key me-2"></i>
                      Change Password
                    </Button>
                  </div>
                </Form>

                {/* Digital Signature Section */}
                <div className="border-top pt-4">
                  <h6 className="mb-3">
                    <i className="bi bi-pen me-2"></i>
                    Digital Signature
                  </h6>
                  
                  {signatureError && (
                    <Alert variant="danger" dismissible onClose={() => setSignatureError('')} className="mb-3">
                      {signatureError}
                    </Alert>
                  )}

                  {signatureSuccess && (
                    <Alert variant="success" dismissible onClose={() => setSignatureSuccess('')} className="mb-3">
                      {signatureSuccess}
                    </Alert>
                  )}

                  <Row className="align-items-center">
                    <Col md={4} className="text-center mb-3 mb-md-0">
                      <div className="signature-display">
                        {user?.signature_path ? (
                          <div>
                            <Image 
                              src={getImageUrl(user.signature_path, 'signatures')}
                              alt="Digital Signature" 
                              width={200} 
                              height={80}
                              className="border rounded"
                              style={{ 
                                objectFit: 'contain', 
                                backgroundColor: '#f8f9fa',
                                pointerEvents: 'none',
                                userSelect: 'none',
                                WebkitUserSelect: 'none',
                                MozUserSelect: 'none',
                                msUserSelect: 'none'
                              }}
                              onContextMenu={(e) => e.preventDefault()}
                              onDragStart={(e) => e.preventDefault()}
                              draggable={false}
                              onError={(e) => {
                                console.error('Signature image failed to load:', e.target.src);
                                console.error('API_HOST_BASE:', API_HOST_BASE);
                                console.error('user.signature_path:', user.signature_path);
                                e.target.style.display = 'none';
                                e.target.nextElementSibling.style.display = 'flex';
                              }}
                            />
                            <div className="signature-placeholder" style={{ display: 'none' }}>
                              <i className="bi bi-pen"></i>
                              <small className="d-block text-muted mt-2">Signature failed to load</small>
                            </div>
                          </div>
                        ) : (
                          <div className="signature-placeholder">
                            <i className="bi bi-pen"></i>
                            <small className="d-block text-muted mt-2">No signature uploaded</small>
                          </div>
                        )}
                      </div>
                    </Col>
                    <Col md={8}>
                      <div className="mb-3">
                        <Form.Label>Upload Digital Signature</Form.Label>
                        <Form.Control
                          type="file"
                          accept="image/*"
                          onChange={handleSignatureUpload}
                          ref={signatureInputRef}
                          disabled={signatureLoading}
                        />
                        <Form.Text className="text-muted">
                          Supported formats: JPG, PNG, GIF. Max size: 5MB. Recommended: 400x160px
                        </Form.Text>
                      </div>
                      
                      {user?.signature_path && (
                        <div>
                          <Button 
                            variant="outline-danger" 
                            size="sm"
                            onClick={handleSignatureDelete}
                            disabled={signatureLoading}
                          >
                            {signatureLoading ? (
                              <>
                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                Deleting...
                              </>
                            ) : (
                              <>
                                <i className="bi bi-trash me-2"></i>
                                Delete Signature
                              </>
                            )}
                          </Button>
                        </div>
                      )}
                    </Col>
                  </Row>
                </div>
              </Card.Body>
            </Card>
        </div>
      </div>

      {/* Change Password Modal */}
      <ChangePasswordModal
        show={showChangePasswordModal}
        onHide={() => setShowChangePasswordModal(false)}
        userEmail={user?.email}
      />
    </div>
  );
};

export default Profile;
